/**
 * useTelemetryTimeRange
 * Encapsulates the per-retailer/environment telemetry time-range state shared by
 * the telemetry charts: reads the persisted range from the preferences store
 * (defaulting when no tenant is selected) and returns a setter that persists
 * changes scoped to the active tenant.
 * @packageDocumentation
 */

import { useCallback } from 'react';

import type { TimeRange } from '@/hooks/useMonitoring';
import { useEnvironmentStore, usePreferencesStore } from '@/stores';

// =============================================================================
// Types
// =============================================================================

/**
 * Identifies which telemetry chart a stored time-range preference belongs to.
 * The preferences store keys ranges by an arbitrary chart id string; these are
 * the literals the telemetry charts use.
 */
export type TelemetryChartKey = 'throughput' | 'errorRate' | 'latency' | 'dlqSize';

// =============================================================================
// Hook
// =============================================================================

/**
 * Reads and persists the telemetry time range for a chart, scoped to the
 * currently selected retailer/environment. Defaults to '24h' when no tenant is
 * selected, and ignores updates while no tenant is selected.
 *
 * @param { TelemetryChartKey } key The chart whose time range is read/persisted.
 * @returns { [TimeRange, (r: TimeRange) => void] } The current range and a setter.
 */
export const useTelemetryTimeRange = (key: TelemetryChartKey): [TimeRange, (r: TimeRange) => void] => {
  const currentRetailerCode = useEnvironmentStore((s) => s.currentRetailerCode);
  const currentEnvironment = useEnvironmentStore((s) => s.currentEnvironment);
  const getTelemetryTimeRange = usePreferencesStore((s) => s.getTelemetryTimeRange);
  const setTelemetryTimeRange = usePreferencesStore((s) => s.setTelemetryTimeRange);
  const range =
    currentRetailerCode != null && currentEnvironment != null
      ? getTelemetryTimeRange(currentRetailerCode, currentEnvironment, key)
      : '24h';
  const setRange = useCallback(
    (r: TimeRange) => {
      if (currentRetailerCode == null || currentEnvironment == null) {
        return;
      }

      setTelemetryTimeRange(currentRetailerCode, currentEnvironment, key, r);
    },
    [currentRetailerCode, currentEnvironment, key, setTelemetryTimeRange],
  );

  return [range, setRange];
};
